import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Loader2, Globe, Users, Link2, Link2Off } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { comunidadeService, Comunidade } from '../../services/comunidadeService';
import { grupoService, Grupo } from '../../services/grupoService';

interface ComunidadeDetalheDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  comunidadeId: string | null;
  onAtualizada: (comunidade: Comunidade) => void;
}

export const ComunidadeDetalheDialog: React.FC<ComunidadeDetalheDialogProps> = ({
  open, onOpenChange, comunidadeId, onAtualizada,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [comunidade, setComunidade] = useState<Comunidade | null>(null);

  const [gruposDisponiveis, setGruposDisponiveis] = useState<Grupo[]>([]);
  const [carregandoDisponiveis, setCarregandoDisponiveis] = useState(false);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [vinculando, setVinculando] = useState(false);
  const [desvinculandoId, setDesvinculandoId] = useState<string | null>(null);

  const carregarComunidade = async (id: string) => {
    setLoading(true);
    try {
      const response = await comunidadeService.obter(id);
      setComunidade(response.data.comunidade);
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar comunidade',
        description: error?.response?.data?.message || 'Não foi possível carregar os dados da comunidade.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const carregarGruposDisponiveis = async (instanciaUazapiId: string) => {
    setCarregandoDisponiveis(true);
    try {
      const response = await grupoService.listar({ instanciaUazapiId, disponivelParaComunidade: true, pageSize: 100 });
      setGruposDisponiveis(response.data.grupos || []);
    } catch {
      // silent
    } finally {
      setCarregandoDisponiveis(false);
    }
  };

  useEffect(() => {
    if (open && comunidadeId) {
      carregarComunidade(comunidadeId);
    } else {
      setComunidade(null);
      setGruposDisponiveis([]);
      setSelecionados([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, comunidadeId]);

  useEffect(() => {
    if (comunidade?.instanciaUazapiId) {
      carregarGruposDisponiveis(comunidade.instanciaUazapiId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comunidade?.instanciaUazapiId, comunidade?.gruposVinculados?.length]);

  const handleToggleSelecionado = (grupoId: string, checked: boolean) => {
    setSelecionados((prev) => (checked ? [...prev, grupoId] : prev.filter((id) => id !== grupoId)));
  };

  const handleVincular = async () => {
    if (!comunidade || selecionados.length === 0) return;
    setVinculando(true);
    try {
      const response = await comunidadeService.gerenciarGrupos(comunidade.id, 'add', selecionados);
      const atualizada: Comunidade = response.data.comunidade;
      setComunidade(atualizada);
      onAtualizada(atualizada);
      setSelecionados([]);
      toast({ title: 'Grupos vinculados', description: 'Os grupos selecionados agora fazem parte da comunidade.' });
    } catch (error: any) {
      toast({
        title: 'Erro ao vincular grupos',
        description: error?.response?.data?.message || 'Não foi possível vincular os grupos selecionados.',
        variant: 'destructive',
      });
    } finally {
      setVinculando(false);
    }
  };

  const handleDesvincular = async (grupoId: string) => {
    if (!comunidade) return;
    setDesvinculandoId(grupoId);
    try {
      const response = await comunidadeService.gerenciarGrupos(comunidade.id, 'remove', [grupoId]);
      const atualizada: Comunidade = response.data.comunidade;
      setComunidade(atualizada);
      onAtualizada(atualizada);
      toast({ title: 'Grupo desvinculado' });
    } catch (error: any) {
      toast({
        title: 'Erro ao desvincular grupo',
        description: error?.response?.data?.message || 'Não foi possível desvincular o grupo.',
        variant: 'destructive',
      });
    } finally {
      setDesvinculandoId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-800 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <Globe className="h-5 w-5" />
            {comunidade?.nome || 'Comunidade'}
          </DialogTitle>
        </DialogHeader>

        {loading || !comunidade ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6 py-2">
            <div>
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2 dark:text-gray-200">
                <Users className="h-4 w-4" /> Grupos vinculados ({comunidade.gruposVinculados?.length ?? 0})
              </h3>
              <div className="space-y-1 max-h-[220px] overflow-y-auto rounded-lg border dark:border-slate-700 p-1">
                {(comunidade.gruposVinculados || []).map((g) => (
                  <div key={g.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 text-xs">
                          <Users className="h-3.5 w-3.5" />
                        </AvatarFallback>
                      </Avatar>
                      <p className="text-sm font-medium truncate dark:text-gray-200">{g.nome}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 dark:text-red-400"
                      disabled={desvinculandoId === g.id}
                      onClick={() => handleDesvincular(g.id)}
                    >
                      {desvinculandoId === g.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2Off className="h-4 w-4" />}
                    </Button>
                  </div>
                ))}
                {(!comunidade.gruposVinculados || comunidade.gruposVinculados.length === 0) && (
                  <p className="text-sm text-center text-muted-foreground py-4">Nenhum grupo vinculado ainda.</p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2 dark:text-gray-200">
                <Link2 className="h-4 w-4" /> Vincular grupos existentes
              </h3>
              <div className="space-y-1 max-h-[220px] overflow-y-auto rounded-lg border dark:border-slate-700 p-1">
                {carregandoDisponiveis ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : gruposDisponiveis.length === 0 ? (
                  <p className="text-sm text-center text-muted-foreground py-4">
                    Nenhum grupo disponível nesta instância. Sincronize grupos na aba "Grupos".
                  </p>
                ) : (
                  gruposDisponiveis.map((g) => (
                    <label key={g.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer">
                      <Checkbox
                        checked={selecionados.includes(g.id)}
                        onCheckedChange={(checked) => handleToggleSelecionado(g.id, checked === true)}
                      />
                      <span className="text-sm dark:text-gray-200">{g.nome}</span>
                    </label>
                  ))
                )}
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={handleVincular} disabled={vinculando || selecionados.length === 0} className="bg-green-600 hover:bg-green-700">
                  {vinculando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                  Vincular selecionados ({selecionados.length})
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComunidadeDetalheDialog;
